import {AfterViewInit, Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {BsModalRef, BsModalService} from 'ngx-bootstrap';
import {BsModalEventsEmitter} from 'src/app/modules/shared/events/bsModal.events';
import {LocalisationService} from 'src/app/modules/shared/services/localisation.service';
import {UsersService} from '../../../../shared/services/users.service';
import {MealsComponent} from '../../meals/meals.component';
import {NotificationsService} from '../../../../shared/services/notification.service';
import {DiaryRecipeViewModel} from '../../../../shared/viewModels/diaryRecipe.viewModel';
import {DiaryService} from '../../../../shared/services/diary.service';
import {PricingService} from '../../../../pricing/services/pricing.service';
import {UpgradeComponent} from '../../../../shared/components/modals/upgrade/upgrade.component';
import {TrainingViewModel} from '../../../../shared/viewModels/training.viewModel';
import {TrainingActivityViewModel} from '../../../../shared/viewModels/trainingActivity.viewModel';
import {TrainingDurationViewModel} from '../../../../shared/viewModels/trainingDuration.viewModel';
import {forkJoin} from 'rxjs';
import {AppLoadingEventsEmitter} from '../../../../shared/events/loading.events';
import * as moment from 'moment';

@Component({
    selector: 'app-diary-training',
    templateUrl: './diary-training.component.html',
    styleUrls: ['./diary-training.component.scss']
})
export class DiaryTrainingComponent implements OnInit, AfterViewInit {
    private readonly mobileWidth: number = 576;

    @ViewChild('wrapper') wrapper: ElementRef<HTMLElement>;

    diaryRecipe: DiaryRecipeViewModel;
    mealsComponent: MealsComponent;

    isAllowed = false;
    upgradeModal: BsModalRef;

    trainings: TrainingViewModel[] = [];
    userPlannedTrainings: TrainingViewModel[] = [];

    trainingActivities: TrainingActivityViewModel[];
    trainingDurations: TrainingDurationViewModel[];
    hasCustomDailyTrainings = false;

    constructor(
        public readonly bsModalRef: BsModalRef,
        private readonly bsModalService: BsModalService,
        private readonly bsModalEventsEmitter: BsModalEventsEmitter,
        private readonly usersService: UsersService,
        private readonly localisationService: LocalisationService,
        private readonly diaryService: DiaryService,
        private readonly pricingService: PricingService,
        private readonly notificationService: NotificationsService,
        private readonly appLoadingEventsEmitter: AppLoadingEventsEmitter,
    ) {
    }

    ngOnInit() {
        this.appLoadingEventsEmitter.broadcastAppIsLoading(true);

        const interval = setInterval(() => {
            if (this.mealsComponent) {
                clearInterval(interval);

                forkJoin(
                    this.usersService.getTrainings(),
                    this.diaryService.getDiaryTrainings(this.mealsComponent.date),
                    this.pricingService.isPermissionAllowed(this.pricingService.permissions['DAY_TRAINING']),
                    this.usersService.getUserTrainingActivities(),
                    this.usersService.getUserTrainingDurations()
                )
                    .subscribe(([userPlannedTrainings, diaryTrainings, isAllowed, trainingActivities, trainingDurations]) => {
                        this.isAllowed = isAllowed;
                        this.userPlannedTrainings = userPlannedTrainings;

                        this.trainingActivities = trainingActivities;
                        this.trainingDurations = trainingDurations;

                        if (diaryTrainings.length > 0) {
                            this.trainings = diaryTrainings;
                            this.hasCustomDailyTrainings = true;
                        } else {
                            this.prepareUserPlannedTrainings();
                        }

                        if (this.trainings.length <= 0) {
                            this.trainings.push(new TrainingViewModel());
                            this.hasCustomDailyTrainings = true;
                        }

                        this.appLoadingEventsEmitter.broadcastAppIsLoading(false);
                    });
            }
        }, 100);
    }

    ngAfterViewInit() {
        if (window.innerWidth <= this.mobileWidth) {
            this.toFullscreen();
        }
    }

    openUpgradeModal() {
        this.upgradeModal = this.bsModalService.show(UpgradeComponent, {
            backdrop: true
        });

        this.bsModalEventsEmitter.broadcastBsModalStateChanged(this.upgradeModal);
    }

    private toFullscreen() {
        const modalDialog = this.wrapper.nativeElement.parentElement.parentElement.parentElement;
        const modalContent = this.wrapper.nativeElement.parentElement.parentElement;
        const modal = this.wrapper.nativeElement.parentElement;

        modalDialog.style.borderRadius = '0';
        modalDialog.style.display = 'flex';
        modalDialog.style.margin = '0';
        modalDialog.style.padding = '0';
        modalDialog.style.width = '100%';
        modalDialog.style.maxWidth = 'unset';
        modalDialog.style.minHeight = '100%';
        modalDialog.style.height = '100%';
        modalContent.style.borderRadius = '0';
        modal.style.display = 'block';
        modal.style.height = '100%';
    }

    addTraining() {
        this.trainings.push(new TrainingViewModel());
    }

    removeTraining(index: number) {
        this.trainings.splice(index, 1);

        if (this.trainings.length === 0 && this.hasCustomDailyTrainings === true) {
            this.prepareUserPlannedTrainings();

            if (this.trainings.length <= 0) {
                this.trainings.push(new TrainingViewModel());
            } else {
                this.hasCustomDailyTrainings = false;
            }
        } else {
            if (this.trainings.length === 0) {
                this.trainings.push(new TrainingViewModel());
            }
        }
    }

    duplicateTraining(index: number) {
        this.trainings.push({
            training_activity: this.trainings[index].training_activity,
            training_duration: this.trainings[index].training_duration,
        });
    }

    onTrainingChange(event, index: number) {
        // tslint:disable-next-line:radix
        this.trainings[index].training_activity = this.trainingActivities.find(ta => ta.id === parseInt(event.target.value));
    }

    onDurationChange(event, index: number) {
        // tslint:disable-next-line:radix
        this.trainings[index].training_duration = this.trainingDurations.find(td => td.id === parseInt(event.target.value));
    }

    isToday(weekDay: string) {
        if (weekDay === null || typeof weekDay === 'undefined') {
            return true;
        }

        return moment(this.mealsComponent.date).format('ddd').toUpperCase() === weekDay.toUpperCase();
    }

    save() {
        if (this.isAllowed === false) {
            this.openUpgradeModal();
            return;
        }

        this.appLoadingEventsEmitter.broadcastAppIsLoading(true);

        const updateDiaryTrainings = () => {
            this.diaryService.updateDiaryTrainings(this.mealsComponent.date, {trainings: this.convertToTrainingsDTO()})
                .subscribe(() => {
                    this.appLoadingEventsEmitter.broadcastAppIsLoading(false);

                    this.notificationService.success(this.localisationService.localise('diary_training_update_success'));

                    this.bsModalRef.hide();
                    this.mealsComponent.init().subscribe();
                }, () => {
                    this.appLoadingEventsEmitter.broadcastAppIsLoading(false);
                });
        };

        if (!this.mealsComponent.diary) {
            this.mealsComponent.createDiary().subscribe(() => {
                updateDiaryTrainings();
            });
        } else {
            updateDiaryTrainings();
        }
    }

    convertToTrainingsDTO() {
        const dtoData = [];

        this.trainings.map(training => {
            if (training.training_activity && training.training_duration) {
                dtoData.push({
                    'training_activity_id': training.training_activity.id,
                    'training_duration_id': training.training_duration.id,
                });
            }

        });

        return dtoData;
    }

    private prepareUserPlannedTrainings() {
        for (const i in this.userPlannedTrainings) {
            if (this.isToday(this.userPlannedTrainings[i].day)) {
                this.trainings.push(this.userPlannedTrainings[i]);
            }
        }
    }

    disableTrainingToday() {
        this.appLoadingEventsEmitter.broadcastAppIsLoading(true);

        const disableTrainingsToday = () => {
            this.diaryService.updateDiaryDisableTraining(!this.mealsComponent.diary.disable_training_today, this.mealsComponent.date)
                .subscribe(() => {
                    this.appLoadingEventsEmitter.broadcastAppIsLoading(false);

                    if (!this.mealsComponent.diary.disable_training_today === true) {
                        this.notificationService.success(this.localisationService.localise('disable_training_today_msg_disable'));
                    } else {
                        this.notificationService.success(this.localisationService.localise('disable_training_today_msg_enable'));
                    }

                    this.mealsComponent.init().subscribe();
                }, () => {
                    this.appLoadingEventsEmitter.broadcastAppIsLoading(false);
                });
        };

        if (!this.mealsComponent.diary) {
            this.mealsComponent.createDiary().subscribe(() => {
                disableTrainingsToday();
            });
        } else {
            disableTrainingsToday();
        }
    }

    showDisableTrainingTodayBtn() {
        if (!this.mealsComponent) {
            return true;
        }

        return typeof this.mealsComponent.diary === 'undefined' || this.mealsComponent.diary.disable_training_today === false;
    }

    showEnableTrainingTodayBtn() {
        if (!this.mealsComponent) {
            return false;
        }

        return typeof this.mealsComponent.diary !== 'undefined' && this.mealsComponent.diary.disable_training_today === true;
    }

    isIOS() {
        return [
                'iPad Simulator',
                'iPhone Simulator',
                'iPod Simulator',
                'iPad',
                'iPhone',
                'iPod'
            ].includes(navigator.platform)
            // iPad on iOS 13 detection
            || (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
    }
}
